import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { trackEvent } from "../../lib/analytics";
import { Upload, X, Loader2, CheckCircle2, ImagePlus } from "lucide-react";

const PART_OPTIONS = ["Side Mirror", "Windshield", "Backglass", "Other"];
const inputCls = "w-full h-11 px-3 bg-white/5 border border-white/15 text-white text-sm rounded-md focus:outline-none focus:border-red-500 transition placeholder:text-zinc-500";

/**
 * Shared inquiry form used by the Contact page and the Fitment Assistance
 * modal. inquiryType drives the GA4 submit event + payload tag. prefill
 * auto-populates vehicle/product context.
 */
export default function InquiryForm({ inquiryType = "contact", prefill = {}, onSubmitted }) {
  const [f, setF] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle_year: prefill.vehicle_year || "",
    vehicle_make: prefill.vehicle_make || "",
    vehicle_model: prefill.vehicle_model || "",
    trim: prefill.trim || "",
    vin: "",
    part_needed: prefill.part_needed || "",
    message: "",
  });
  const [smsConsent, setSmsConsent] = useState(false);
  const [photos, setPhotos] = useState([]); // [{url, filename}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > 8) {
      setError("You can attach up to 8 photos.");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((file) => fd.append("files", file));
      const { data } = await api.post("/inquiries/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, ...data.files]);
      trackEvent("inquiry_photo_upload", { count: data.count, inquiry_type: inquiryType });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (url) => setPhotos((p) => p.filter((x) => x.url !== url));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post("/inquiries", {
        ...f,
        sms_consent: smsConsent,
        photos: photos.map((p) => p.url),
        inquiry_type: inquiryType,
        product_name: prefill.product_name || null,
        product_url: prefill.product_url || null,
      });
      const submitParams = {
        inquiry_type: inquiryType,
        part_needed: f.part_needed || undefined,
        product_name: prefill.product_name || undefined,
        product_id: prefill.product_id || undefined,
        vehicle_year: f.vehicle_year || undefined,
        vehicle_make: f.vehicle_make || undefined,
        vehicle_model: f.vehicle_model || undefined,
        photo_count: photos.length,
      };
      trackEvent(inquiryType === "fitment" ? "fitment_request_submit" : "contact_form_submit", submitParams);
      // Fitment-assistance CTA conversion event (paired with fitment_help_click).
      if (inquiryType === "fitment") trackEvent("fitment_help_submit", submitParams);
      setDone(true);
      onSubmitted?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="inquiry-success">
        <CheckCircle2 size={42} className="text-emerald-500 mx-auto mb-4" />
        <h3 className="font-display text-2xl mb-2">Thank you — we got your request</h3>
        <p className="text-zinc-400 text-sm max-w-md mx-auto">
          Our team will verify fitment and respond to <span className="text-white">{f.email}</span> as soon as possible.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name *"><input data-testid="inquiry-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Your name" /></Field>
        <Field label="Email *"><input data-testid="inquiry-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="you@email.com" /></Field>
        <Field label="Mobile Phone (optional)"><input data-testid="inquiry-phone" type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="(888) 785-1288" /></Field>
        <Field label="Vehicle Year"><input data-testid="inquiry-year" value={f.vehicle_year} onChange={(e) => set("vehicle_year", e.target.value)} className={inputCls} placeholder="2021" /></Field>
        <Field label="Vehicle Make"><input data-testid="inquiry-make" value={f.vehicle_make} onChange={(e) => set("vehicle_make", e.target.value)} className={inputCls} placeholder="Porsche" /></Field>
        <Field label="Vehicle Model"><input data-testid="inquiry-model" value={f.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} className={inputCls} placeholder="911" /></Field>
        <Field label="Trim"><input data-testid="inquiry-trim" value={f.trim} onChange={(e) => set("trim", e.target.value)} className={inputCls} placeholder="Carrera S" /></Field>
        <Field label="VIN"><input data-testid="inquiry-vin" value={f.vin} onChange={(e) => set("vin", e.target.value)} className={inputCls} placeholder="Optional — helps verify fitment" /></Field>
        <Field label="Part Needed">
          <select data-testid="inquiry-part" value={f.part_needed} onChange={(e) => set("part_needed", e.target.value)} className={inputCls}>
            <option value="">Select…</option>
            {PART_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
      </div>

      {prefill.product_name && (
        <div className="text-xs text-zinc-400 bg-white/5 border border-white/10 rounded-md px-3 py-2" data-testid="inquiry-product-context">
          Regarding: <span className="text-white">{prefill.product_name}</span>
        </div>
      )}

      <Field label="Message">
        <textarea data-testid="inquiry-message" value={f.message} onChange={(e) => set("message", e.target.value)} rows={4} className={inputCls.replace("h-11", "") + " py-2 resize-none"} placeholder="Tell us about the part you need or any fitment questions…" />
      </Field>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Photos (optional)</div>
        <p className="text-xs text-zinc-500 mb-2">Attach vehicle, existing part, VIN sticker, or damage photos. JPG/PNG/WEBP, up to 8.</p>
        <div className="flex flex-wrap gap-3">
          {photos.map((p) => (
            <div key={p.url} className="relative w-20 h-20 rounded-md overflow-hidden border border-white/10" data-testid="inquiry-photo-thumb">
              <img src={p.url} alt={p.filename} className="w-full h-full object-cover" />
              <button type="button" onClick={() => removePhoto(p.url)} className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 hover:bg-red-600" aria-label="Remove photo">
                <X size={12} />
              </button>
            </div>
          ))}
          {photos.length < 8 && (
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="inquiry-photo-add" disabled={uploading} className="w-20 h-20 rounded-md border border-dashed border-white/20 flex flex-col items-center justify-center text-zinc-400 hover:border-red-500 hover:text-white transition disabled:opacity-50">
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <><ImagePlus size={18} /><span className="text-[10px] mt-1">Add</span></>}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={onPickFiles} className="hidden" data-testid="inquiry-photo-input" />
        </div>
      </div>

      {error && <p className="text-sm text-red-400" data-testid="inquiry-error">{error}</p>}

      <label className="flex items-start gap-3 cursor-pointer bg-white/[0.03] border border-white/10 rounded-md p-3" data-testid="inquiry-sms-consent-label">
        <input
          type="checkbox"
          data-testid="inquiry-sms-consent"
          checked={smsConsent}
          onChange={(e) => setSmsConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-red-600 cursor-pointer"
        />
        <span className="text-[11px] leading-relaxed text-zinc-400">
          I agree to receive conversational text messages from Lecis Auto Glass regarding my inquiry, order,
          appointment, or account. Message frequency may vary. Message and data rates may apply. Reply STOP to
          opt out at any time. Reply HELP for assistance. Your mobile number will not be shared with third
          parties for marketing or promotional purposes. View our{" "}
          <Link to="/privacy-policy" className="text-zinc-200 underline underline-offset-2 hover:text-white" data-testid="inquiry-sms-privacy-link">Privacy Policy</Link>{" "}and{" "}
          <Link to="/terms-conditions" className="text-zinc-200 underline underline-offset-2 hover:text-white" data-testid="inquiry-sms-terms-link">Terms &amp; Conditions</Link>.
        </span>
      </label>

      <button type="submit" disabled={submitting} data-testid="inquiry-submit" className="w-full h-12 bg-red-600 hover:bg-red-700 text-white text-sm font-medium tracking-wide rounded-md transition disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Upload size={16} /> {inquiryType === "fitment" ? "Request fitment help" : "Send inquiry"}</>}
      </button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
